import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Mention, MentionsInput } from "react-mentions";
import { useParams } from "react-router-dom";

import styled from "styled-components";

import "./PDFChatInputStyle.css";

import Icon from "components/atoms/Icon";
import Tooltip from "components/atoms/Tooltip";
import Button from "components/atoms/button";
import Checkbox from "components/atoms/input/Checkbox";
import Text from "components/atoms/text/Text";

import PDFChatFileAnalysisView from "components/organisms/review/PDFChatFileAnalysisView";
import PDFChatFileUploadView from "components/organisms/review/PDFChatFileUploadView";
import PDFChatMessage from "components/organisms/review/PDFChatMessage";

import Constants from "utils/constants";
import { mentionCandidates } from "utils/mention";
import { isReadonlyUser } from "utils/user";
import { genericEnterKeyDown } from "utils/validation";

const Container = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    border-left: solid 1px var(--color-Outline);
    overflow: hidden;

    display: flex;
    flex-direction: column;
`;

const Header = styled.div`
    width: 100%;
    min-height: 40px;
    padding: 0 4px;

    display: flex;
    align-items: center;
    justify-content: space-between;

    background-color: var(--color-BaseBlue);
    border-bottom: solid 1px var(--color-Outline);
    overflow: hidden;
    transition: all 120ms ease-in;
`;

const HeaderButton = styled.button`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 32px;
    height: 32px;
    background-color: var(--color-BaseBlue);
    border-radius: 4px;
    border: transparent;
    outline: none;
    transform: ${({ fold }) => (fold ? "rotate(0deg)" : "rotate(180deg)")};
    transition: background-color 120ms ease-in;

    &:hover {
        background-color: var(--color-ButtonHover4);
    }
`;

const HeaderTitle = styled(Text).attrs({
    variant: "body-md-500",
    color: "var(--color-SubBlack)",
})``;

const HeaderPlaceholder = styled.div`
    width: 32px;
    height: 32px;
`;

const BodyContainer = styled.div`
    position: relative;
    overflow-x: hidden;
    overflow-y: auto;
    width: 100%;
    flex: 1 1 0%;
`;

const SlideFadeContainer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
`;

function BodyContentContainer({ active, zIndex, enterFrom, leaveTo, children, ...props }) {
    const [shouldRender, setShouldRender] = useState(active);
    const [transitionState, setTransitionState] = useState({
        opacity: active ? 1 : 0,
        translate: "0%",
    });

    useEffect(() => {
        let timeout;

        // 입장 (active가 true)
        if (active) {
            setShouldRender(true);
            // 입장 시작점 세팅
            let fromX = "0%";
            if (enterFrom === "left") fromX = "100%";
            if (enterFrom === "right") fromX = "-100%";
            setTransitionState({ opacity: 0, translate: fromX });
            // 다음 tick에 트랜지션 시작
            setTimeout(() => {
                setTransitionState({ opacity: 1, translate: "0%" });
            }, 20); // 0~20ms 권장 (트랜지션 트리거)
        } else {
            // 퇴장 방향 세팅
            let toX = "0%";
            if (leaveTo === "left") toX = "-100%";
            if (leaveTo === "right") toX = "100%";
            setTransitionState({ opacity: 0, translate: toX });
            // 트랜지션 끝나면 unmount
            timeout = setTimeout(() => setShouldRender(false), 400);
        }
        return () => clearTimeout(timeout);
    }, [active, enterFrom, leaveTo]);

    if (!shouldRender) return null;

    return (
        <SlideFadeContainer
            style={{
                opacity: transitionState.opacity,
                transform: transitionState.translate === "0%" ? "none" : `translateX(${transitionState.translate})`,
                zIndex,
                transition: "opacity 400ms ease-in-out, transform 400ms ease-in-out",
            }}
            {...props}
        >
            {children}
        </SlideFadeContainer>
    );
}

const ThreadListTitle = styled(Text).attrs({
    variant: "body-md-500",
    color: "var(--color-SubBlack)",
    withBarType: true,
})`
    margin: 8px;
    padding: 8px;
`;

const SearchResultText = styled(Text).attrs({
    variant: "body-md-400",
    color: "var(--color-SubBlack)",
})`
    padding: 16px;
    text-align: center;
`;

const FooterContainer = styled.div`
    position: sticky;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    background-color: transparent;
`;

const FooterChatBox = styled.div`
    margin: 8px 8px 16px 8px;
    width: 100%;
    height: auto;
    background-color: var(--color-White);
    border: solid 1px var(--color-Outline);
    border-radius: 4px;
    box-shadow: 1px 1px 8px rgba(0, 0, 0, 0.1);
`;

const FooterChatSelectedBoxContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    background-color: var(--color-White);
    border-top-right-radius: 5px;
    border-top-left-radius: 5px;
`;

const FooterChatSelectedBoxHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 4px;
    width: 100%;
    background-color: var(--color-BaseBlue);
`;

const FooterChatSelectedBoxTitle = styled(Text).attrs({
    variant: "body-md-400",
    color: "var(--color-Black)",
})``;

const SelectedBoxContainerInnerWrap = styled.div`
    display: flex;
    padding: 8px;
    gap: 8px;
    width: 100%;
    border-bottom: solid 1px var(--color-Outline);
`;

const SelectedBoxImage = styled.div`
    position: relative;
    float: left;
    margin-right: 8px;

    width: 48px;
    height: 48px;
    border: 1px solid
        ${({ color }) => {
            return color;
        }};

    overflow: hidden;
`;

const SelectedBoxPageText = styled.div`
    position: absolute;
    inset: 0;

    display: flex;
    justify-content: center;
    align-items: center;

    border-left: 10px solid
        ${({ color }) => {
            return color;
        }};

    opacity: 1;
    color: var(--color-White);
    background-color: var(--color-Black);

    &:hover {
        cursor: pointer;

        opacity: 0.5;
        color: transparent;
        background-color: transparent;
    }
`;

const SelectedBoxColorPalleteWrap = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const SelectedBoxColorItem = styled.div`
    width: 16px;
    height: 16px;
    border-radius: 8px;
    border: ${({ $active }) => ($active ? "solid 2px var(--color-Black)" : "solid 2px var(--color-White)")};
    background-color: ${({ color }) => color};
    cursor: pointer;
`;

const ChatInputWrap = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 8px 4px;
`;

const MentionInputWrap = styled.div`
    width: calc(100% - 40px);
`;

const ChatInputOptionWrap = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 4px;
    width: 100%;
    background-color: var(--color-BaseBlue);
    border-bottom-right-radius: 5px;
    border-bottom-left-radius: 5px;
`;

function PDFChatSidebar(props) {
    const { space_uuid } = useParams();
    const openNewTab = (path) => window.open(`/${space_uuid}${path}`, "_blank", "noopener noreferrer");
    const VIEW_THREAD_LIST = "thread_list";
    const VIEW_THREAD_DETAIL = "thread_detail";
    const VIEW_SEARCH = "search";
    const [currentView, setCurrentView] = useState(VIEW_THREAD_LIST);

    const [inputMessage, setInputMessage] = useState("");
    const [inputMessagePlaceHolder, setInputMessagePlaceHolder] = useState("");
    const textareaRef = useRef();

    const handleInputMessage = (q) => {
        let inputText = q.trim();
        if (!inputText) {
            return;
        }

        let req_message = {
            autoResponse: autoResponse,
            content: inputText,
            reviewBoxId: props.selectedBox?.id || null,
            reviewBoxColor: props.selectedBox?.id ? selectedBoxColor : null,
            newReviewBoxData: props.selectedBox || null,
            parentMessageId: lastVisibleMessageId,
        };

        setInputMessage("");
        props.setSelectedBox && props.setSelectedBox(null);
        props.setSearchBox && props.setSearchBox(null);
        textareaRef.current.blur();

        props.chatStart(req_message);
    };

    // region: 스크롤, 포커스 관련 영역
    const bodyScrollRef = useRef();
    const [focusedMessageId, setFocusedMessageId] = useState(null);
    useEffect(() => {
        // 새로운 메시지 스트리밍 중 스크롤 처리
        if (props.autoScrollToEnd) {
            if (bodyScrollRef.current) {
                bodyScrollRef.current.scrollTop = bodyScrollRef.current.scrollHeight;
            }
        }
    }, [props.autoScrollToEnd, props.threads]);

    useLayoutEffect(() => {
        // 전환/레이아웃 플러시 후 최하단으로
        const raf1 = requestAnimationFrame(() => {
            const raf2 = requestAnimationFrame(() => {
                if (focusedMessageId) {
                    return;
                }
                if (bodyScrollRef.current) {
                    if (currentView === VIEW_THREAD_DETAIL) {
                        bodyScrollRef.current.scrollTop = bodyScrollRef.current.scrollHeight;
                    } else {
                        bodyScrollRef.current.scrollTop = 0;
                    }
                }
            });
            // cleanup inner
            return () => cancelAnimationFrame(raf2);
        });

        return () => cancelAnimationFrame(raf1);
    }, [currentView, focusedMessageId]);

    const enterThreadDetailView = (thread) => {
        props.setSelectedThread(thread);
    };
    const leaveThreadDetailView = () => {
        props.setSelectedThread(null);
        setFocusedMessageId(props.selectedThread.id);
    };
    useEffect(() => {
        if (!focusedMessageId) return;
        const t = setTimeout(() => setFocusedMessageId(null), 3000);
        return () => clearTimeout(t);
    }, [focusedMessageId]);

    useEffect(() => {
        if (props.focusedMessageId) setFocusedMessageId(props.focusedMessageId);
    }, [props.focusedMessageId]);

    // 질문 입력창 크기 조절
    useEffect(() => {
        if (inputMessage) {
            const textarea = textareaRef.current;
            textarea.style.height = `${textarea.scrollHeight}px`;
        }
    }, [inputMessage]);

    // selectedBox 의 color 설정
    const [selectedBoxColor, setSelectedBoxColor] = useState("var(--color-HighlightRed)");

    useEffect(() => {
        // selectedBox가 바뀌면 입력창에 포커스
        if (props.selectedBox && textareaRef.current) {
            textareaRef.current.focus();
        }
    }, [props.selectedBox]);

    // 현재 뷰 상태 유지: 검색 > 상세 > 목록 우선순위
    useEffect(() => {
        const nextView = props.searchBox ? VIEW_SEARCH : props.selectedThread ? VIEW_THREAD_DETAIL : VIEW_THREAD_LIST;
        setCurrentView(nextView);

        if (nextView === VIEW_THREAD_DETAIL) {
            setInputMessagePlaceHolder("현재의 채팅 기록에 메시지 추가");
        } else {
            setInputMessagePlaceHolder("새로운 채팅 기록 추가");
        }
    }, [props.selectedBox, props.searchBox, props.selectedThread]);

    //region: search
    const searchResultMessages = props.selectedBox
        ? getMessagesBySelectedBox(props.threads, props.selectedBox)
        : props.searchBox
          ? getMessagesBySearchBox(props.threads, props.searchBox)
          : [];

    const leaveSearchResultContainer = () => {
        props.setSelectedBox && props.setSelectedBox(null);
        props.setSearchBox && props.setSearchBox(null);
    };

    function getMessagesBySelectedBox(threads, selectedBox) {
        if (!selectedBox) return [];
        const { bbox, page } = selectedBox;
        return threads
            .filter((thread) => !thread.is_deleted)
            .flatMap((thread) => thread.messages)
            .filter(
                (msg) =>
                    msg.review_box &&
                    msg.review_box.page === page &&
                    JSON.stringify(msg.review_box.data?.bbox) === JSON.stringify(bbox) &&
                    msg.is_deleted === false,
            );
    }

    function getMessagesBySearchBox(threads, searchBox) {
        if (!searchBox) return [];
        return threads
            .filter((thread) => !thread.is_deleted)
            .flatMap((thread) => thread.messages)
            .filter((msg) => msg.review_box?.id === searchBox.id && msg.is_deleted === false);
    }

    const handleMessageClick = (message) => {
        // 1) search container 닫기
        props.setSelectedBox(null);
        props.setSearchBox(null);

        // 2) 해당 메시지가 속한 thread 찾기
        const thread = props.threads.find((t) => t.messages.some((m) => m.id === message.id));
        if (thread) {
            enterThreadDetailView(thread); // 3) thread detail view 진입
            setFocusedMessageId(message.id); // 4) 포커스 메시지 id 저장
        }
    };

    // options
    const [autoResponse, setAutoResponse] = useState(true);

    // region: 선택 스레드 메시지 랜더링

    // 선택 스레드 메시지 맵핑 생성
    const [messageMapByParent, setMessageMapByParent] = useState({});
    useEffect(() => {
        if (props.selectedThread) {
            const newMapByParent = {};
            props.selectedThread.messages.forEach((msg) => {
                const parent = msg.parent || null;
                if (!newMapByParent[parent]) {
                    newMapByParent[parent] = [];
                }
                newMapByParent[parent].push(msg);
            });
            setMessageMapByParent(newMapByParent);
        }
    }, [props.selectedThread]);

    // 선택 스레드 메시지 랜더(재귀)
    const [messageVersionMapByParent, setMessageVersionMapByParent] = useState({}); // 버전 선택 상태 관리 (부모ID별 선택된 버전)
    function handleChangeVersion(parentId, direction) {
        const children = messageMapByParent[parentId];
        if (!children || !children.length) return;

        const selectedVersion = messageVersionMapByParent[parentId] || children[children.length - 1].version;
        let targetVersion = selectedVersion + direction;
        if (targetVersion < 1) targetVersion = 1;
        if (targetVersion > children[children.length - 1].version)
            targetVersion = children[children.length - 1].version;
        setMessageVersionMapByParent((prev) => ({ ...prev, [parentId]: targetVersion }));
    }

    function renderSelectedThreadMessages({ parentId = null, renderMessage }) {
        const message = pickMessageByParentId(parentId);
        if (!message) return null;

        const messageComponent = renderMessage(message);

        if (message.is_deleted) {
            // 삭제된 메시지는 마지막이라고 보이더라도 하위는 렌더하지 않음
            return messageComponent;
        } else {
            return (
                <>
                    {messageComponent}
                    {renderSelectedThreadMessages({
                        parentId: message.id,
                        renderMessage,
                    })}
                </>
            );
        }
    }

    // 화면에 나타나는 마지막 메시지를 state 로 유지 (메시지 추가시 parent 로 활용)
    const [lastVisibleMessageId, setLastVisibleMessageId] = useState(null);

    // 부모 id 기준으로, 현재 버전 선택 상태를 반영한 메시지 하나를 리턴
    const pickMessageByParentId = useCallback(
        (parentId = null) => {
            const children = messageMapByParent[parentId];
            if (!children || children.length === 0) return null;

            // 기본은 마지막 child
            let message = children[children.length - 1];

            // 버전 선택되어 있으면 그 버전 우선
            if (messageVersionMapByParent[parentId]) {
                const v = messageVersionMapByParent[parentId];
                const found = children.find((m) => m.version === v);
                if (found) message = found;
            }
            return message;
        },
        [messageMapByParent, messageVersionMapByParent],
    );

    // 현재 selectedThread + version 선택을 기준으로, 논리적으로 마지막에 보이는 메시지의 id 계산
    const calcLastVisibleMessageId = useCallback(() => {
        let lastId = null;
        let parentId = null;

        while (true) {
            const message = pickMessageByParentId(parentId);
            if (!message) break;

            if (message.is_deleted) {
                // 마지막 메시지가 삭제된 경우, 바로 위(lastId)를 마지막으로 간주
                break;
            }

            lastId = message.id;
            parentId = message.id;
        }

        return lastId;
    }, [pickMessageByParentId]);
    useEffect(() => {
        if (!props.selectedThread) {
            setLastVisibleMessageId(null);
            return;
        }

        const message_id = calcLastVisibleMessageId();
        setLastVisibleMessageId(message_id);
    }, [calcLastVisibleMessageId, props.selectedThread]);

    // focusedMessageId 가 지정되면, 해당 메시지의 버전을 선택해 트리 구성
    // - focusedMessageId 자신이 속한 parent 에 대해, 그 parent 의 선택 버전을 해당 메시지의 version 으로 맞춘다.
    // - focusedMessageId 의 자식들에 대해서는 messageVersionMapByParent 를 건드리지 않으므로 기본(마지막 버전)으로 표시된다.
    useEffect(() => {
        if (!focusedMessageId || !props.selectedThread) return;

        // 현재 스레드에서 focusedMessageId 에 해당하는 메시지 찾기
        const targetMessage = props.selectedThread.messages.find((m) => m.id === focusedMessageId);
        if (!targetMessage) return;

        const parentId = targetMessage.parent || null;
        const children = messageMapByParent[parentId] || [];
        // parentId 아래의 children 중에서 실제로 동일한 메시지(id 기준)를 찾음
        const targetChild = children.find((m) => m.id === focusedMessageId);
        if (!targetChild) return;

        const targetVersion = targetChild.version;
        if (!targetVersion) return;

        setMessageVersionMapByParent((prev) => {
            // 이미 같은 버전이면 변경 없음
            if (prev[parentId] === targetVersion) return prev;
            return { ...prev, [parentId]: targetVersion };
        });
    }, [focusedMessageId, props.selectedThread, messageMapByParent]);

    // region: 마크업
    if (props.isSidebarFold) {
        return (
            <Container>
                <Header>
                    <Tooltip message={"사이드바 열기"}>
                        <HeaderButton onClick={() => props.setIsSidebarFold(false)}>
                            <Icon name={"sidemenuOpen"} size={"12"} color={"var(--color-Black)"} />
                        </HeaderButton>
                    </Tooltip>
                </Header>
            </Container>
        );
    }

    const renderPdfOpenButton = () => {
        if (props.setCurPage) {
            // 리뷰 페이지만 setCurPage 메소드가 있음에 착안하여, 리뷰페이지에서는 PDF 열기 버튼 미제공
            return <HeaderPlaceholder />;
        }
        return (
            <Button
                onlyText
                width={"60px"}
                height={"24px"}
                bgColor={"var(--color-Button1)"}
                hoverBgColor={"var(--color-ButtonHover1)"}
                fontSize={"small"}
                fontColor={"var(--color-White)"}
                buttonText={"PDF 열기"}
                onClick={(e) => {
                    const review_window = openNewTab(
                        "/reference/" + props.reference.reference_no + "/review/" + props.reference.file_uuid,
                    );
                    const channel = Constants.REVIEW_MESSAGE_CHANNEL;
                    const payload = {
                        page: 1,
                        thread_id: props.selectedThread?.id,
                        message_id: null,
                    };

                    // 자식 윈도우와 handshaking 후 메시지 전송
                    const readyListener = (event) => {
                        if (
                            event.source === review_window &&
                            event.origin === window.location.origin &&
                            event.data?.__channel === channel
                        ) {
                            review_window.postMessage(
                                {
                                    __channel: channel,
                                    payload: payload,
                                },
                                window.location.origin,
                            );
                            window.removeEventListener("message", readyListener);
                        }
                    };
                    window.addEventListener("message", readyListener);
                }}
            />
        );
    };

    return props.reference?.file ? (
        <Container onClick={leaveSearchResultContainer}>
            {/* 사이드바 헤더 */}
            <Header>
                {!props.isSidebarFold && ( // 사이드바 상태에 따라 툴팁이 사라지지 않는 버그 수정
                    <>
                        {currentView === VIEW_THREAD_DETAIL ? (
                            <>
                                <Tooltip message={"채팅 목록으로 이동"}>
                                    <HeaderButton onClick={leaveThreadDetailView}>
                                        <Icon name={"arrowRight"} size={"12"} color={"var(--color-SubBlack)"} />
                                    </HeaderButton>
                                </Tooltip>
                                <HeaderTitle>채팅 상세</HeaderTitle>
                                {renderPdfOpenButton()}
                            </>
                        ) : currentView === VIEW_SEARCH ? (
                            <>
                                <HeaderPlaceholder />
                                <HeaderTitle>검색된 메시지</HeaderTitle>
                                <Tooltip message={"닫기"}>
                                    <HeaderButton onClick={leaveSearchResultContainer}>
                                        <Icon name={"x"} size={"12"} color={"var(--color-SubBlack)"} />
                                    </HeaderButton>
                                </Tooltip>
                            </>
                        ) : (
                            <>
                                <Tooltip message={"사이드바 닫기"}>
                                    <HeaderButton onClick={() => props.setIsSidebarFold(true)}>
                                        <Icon name={"sidemenuClose"} size={"12"} color={"var(--color-Black)"} />
                                    </HeaderButton>
                                </Tooltip>
                                <HeaderTitle>PDF 리뷰</HeaderTitle>
                                {renderPdfOpenButton()}
                            </>
                        )}
                    </>
                )}
            </Header>

            <BodyContainer ref={bodyScrollRef}>
                {/* 스레드 목록 */}
                <BodyContentContainer active={currentView === VIEW_THREAD_LIST} zIndex={1}>
                    <PDFChatFileAnalysisView
                        reference={props.reference}
                        pdfIndexingInfo={props.pdfIndexingInfo}
                        chatStart={props.chatStart}
                        user={props.user}
                    />
                    <ThreadListTitle>PDF 채팅 기록</ThreadListTitle>
                    {props.threads.length === 0 ? (
                        <SearchResultText>채팅 기록이 없습니다.</SearchResultText>
                    ) : (
                        props.threads.map((thread, index) => {
                            if (!thread.is_deleted) {
                                return (
                                    <PDFChatMessage
                                        key={thread.id}
                                        bgColor={"var(--color-ListItem)"}
                                        isThread={true}
                                        thread={thread}
                                        message={thread.messages[0]}
                                        profiles={props.profiles}
                                        myProfileId={props.myProfileId}
                                        enterThreadDetailView={enterThreadDetailView}
                                        setCurPage={props.setCurPage}
                                        setInputPage={props.setInputPage}
                                        setSearchBox={props.setSearchBox}
                                        focused={focusedMessageId === thread.id}
                                        streamingMessageIds={props.streamingMessageIds}
                                        chatDelete={props.chatDelete}
                                        threadUpdateTitle={props.threadUpdateTitle}
                                    />
                                );
                            }
                            return null;
                        })
                    )}
                </BodyContentContainer>

                {/* 스레드 상세 */}
                <BodyContentContainer
                    active={currentView === VIEW_THREAD_DETAIL}
                    zIndex={2}
                    enterFrom={"left"}
                    leaveTo={"right"}
                >
                    {props.selectedThread &&
                        renderSelectedThreadMessages({
                            parentId: null,
                            renderMessage: (message, index) => (
                                <PDFChatMessage
                                    key={message.id}
                                    bgColor={index % 2 === 0 ? "var(--color-White)" : "var(--color-ListItem)"}
                                    isThread={false}
                                    thread={props.selectedThread}
                                    message={message}
                                    profiles={props.profiles}
                                    myProfileId={props.myProfileId}
                                    reference={props.reference}
                                    enterThreadDetailView={enterThreadDetailView}
                                    setCurPage={props.setCurPage}
                                    setSearchBox={props.setSearchBox}
                                    focused={focusedMessageId === message.id}
                                    streamingMessageIds={props.streamingMessageIds}
                                    autoResponse={autoResponse}
                                    chatStart={props.chatStart}
                                    chatDelete={props.chatDelete}
                                    versionCount={(() => {
                                        const siblings = messageMapByParent[message.parent] || [];
                                        return siblings.length > 0 ? siblings.length : 1;
                                    })()}
                                    onChangeVersion={(direction) =>
                                        handleChangeVersion(message.parent || null, direction)
                                    }
                                />
                            ),
                        })}
                </BodyContentContainer>

                {/* 박스 선택(검색) 뷰 */}
                <BodyContentContainer active={currentView === VIEW_SEARCH} zIndex={3}>
                    {searchResultMessages.length === 0 ? (
                        <SearchResultText>검색된 메시지가 없습니다.</SearchResultText>
                    ) : (
                        searchResultMessages.map((message, idx) => (
                            <div
                                key={idx}
                                onClick={() => handleMessageClick(message)}
                                style={{ display: "flex", cursor: "pointer" }}
                            >
                                <PDFChatMessage
                                    bgColor={idx % 2 === 0 ? "var(--color-White)" : "var(--color-ListItem)"}
                                    isThread={false}
                                    thread={null}
                                    message={message}
                                    profiles={props.profiles}
                                    myProfileId={props.myProfileId}
                                    setCurPage={props.setCurPage}
                                    setSearchBox={props.setSearchBox}
                                />
                            </div>
                        ))
                    )}
                </BodyContentContainer>
            </BodyContainer>
            <FooterContainer>
                <FooterChatBox
                    onClick={(e) => {
                        // 푸터 부분 클릭했을 때, Container 의 onClick 방지
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                >
                    {props.selectedBox ? (
                        <FooterChatSelectedBoxContainer>
                            <FooterChatSelectedBoxHeader>
                                <FooterChatSelectedBoxTitle>선택된 박스</FooterChatSelectedBoxTitle>
                                <Tooltip message={"선택 취소"}>
                                    <Button
                                        onlyIcon
                                        margin="0 4px 0 0"
                                        buttonSize="10px"
                                        bgColor="transparent"
                                        hoverBgColor="transparent"
                                        onClick={(e) => {
                                            props.setSelectedBox(null);
                                        }}
                                    >
                                        <Icon name={"x"} size={"8"} color={"var(--color-SubBlack)"} />
                                    </Button>
                                </Tooltip>
                            </FooterChatSelectedBoxHeader>
                            <SelectedBoxContainerInnerWrap>
                                <SelectedBoxImage color={selectedBoxColor}>
                                    <SelectedBoxPageText color={selectedBoxColor}>
                                        p.{props.selectedBox.page}
                                    </SelectedBoxPageText>
                                    <img
                                        src={props.selectedBox.box_image.base64}
                                        alt=""
                                        style={{
                                            width: props.selectedBox.box_image.width / 3,
                                            height: props.selectedBox.box_image.height / 3,
                                        }}
                                    />
                                </SelectedBoxImage>
                                <SelectedBoxColorPalleteWrap>
                                    {[
                                        "var(--color-HighlightRed)",
                                        "var(--color-HighlightBlue)",
                                        "var(--color-HighlightYellow)",
                                        "var(--color-HighlightGreen)",
                                        "var(--color-HighlightPurple)",
                                    ].map((color, index) => {
                                        return (
                                            <SelectedBoxColorItem
                                                key={index}
                                                color={color}
                                                onClick={() => setSelectedBoxColor(color)}
                                                $active={selectedBoxColor === color}
                                            />
                                        );
                                    })}
                                </SelectedBoxColorPalleteWrap>
                            </SelectedBoxContainerInnerWrap>
                        </FooterChatSelectedBoxContainer>
                    ) : (
                        <FooterChatSelectedBoxContainer>
                            <FooterChatSelectedBoxHeader>
                                <FooterChatSelectedBoxTitle>
                                    {currentView === VIEW_THREAD_LIST ? "새로운 채팅" : "새로운 메시지"}
                                </FooterChatSelectedBoxTitle>
                            </FooterChatSelectedBoxHeader>
                        </FooterChatSelectedBoxContainer>
                    )}
                    <ChatInputWrap>
                        <MentionInputWrap>
                            <MentionsInput
                                autoFocus
                                value={inputMessage}
                                onChange={(event, newValue) => setInputMessage(newValue)}
                                className="mentions"
                                inputRef={textareaRef}
                                placeholder={inputMessagePlaceHolder}
                                allowSuggestionsAboveCursor={true}
                                markup="@\[__display__\](__id__)"
                                singleLine={false}
                                onKeyDown={(e) => {
                                    genericEnterKeyDown(e, () => {
                                        if (e.shiftKey) {
                                            setInputMessage((prev) => prev + "\n");
                                        } else {
                                            handleInputMessage(inputMessage);
                                        }
                                    });
                                }}
                            >
                                <Mention
                                    trigger="@"
                                    data={mentionCandidates}
                                    className="mentions__mention"
                                    appendSpaceOnAdd={true}
                                    renderSuggestion={(suggestion, search, highlightedDisplay, index, focused) => (
                                        <div
                                            style={{
                                                background: focused ? "var(--color-Base2)" : "white",
                                                color: focused ? "black" : "black",
                                            }}
                                        >
                                            @{highlightedDisplay}
                                        </div>
                                    )}
                                    displayTransform={(id, display) => `@${display}`}
                                />
                            </MentionsInput>
                        </MentionInputWrap>
                        {props.streamingMessageIds.length === 0 ? (
                            <Tooltip message="메시지 전송">
                                <Button
                                    onlyIcon
                                    buttonSize={"32px"}
                                    bgColor={"var(--color-Button1)"}
                                    hoverBgColor={"var(--color-ButtonHover1)"}
                                    borderRadius={"4px"}
                                    onClick={() => {
                                        handleInputMessage(inputMessage);
                                    }}
                                    disabled={inputMessage.trim() === "" || isReadonlyUser(props?.user)}
                                >
                                    <Icon name={"enter"} size={"12"} color={"var(--color-White)"} />
                                </Button>
                            </Tooltip>
                        ) : (
                            <Tooltip message="응답 중지">
                                <Button
                                    onlyIcon
                                    buttonSize={"32px"}
                                    bgColor={"var(--color-Warn)"}
                                    hoverBgColor={"var(--color-WarnHover)"}
                                    borderRadius={"4px"}
                                    onClick={() => {
                                        props.chatStop();
                                    }}
                                >
                                    <Icon name={"x"} size={"12"} color={"var(--color-White)"} />
                                </Button>
                            </Tooltip>
                        )}
                    </ChatInputWrap>
                    <ChatInputOptionWrap>
                        <Tooltip message={"메시지에 @PREP 멘션을 자동으로 추가합니다."}>
                            <Checkbox
                                label={"AI 자동 응답"}
                                size={"regular"}
                                onChange={() => setAutoResponse(!autoResponse)}
                                checked={autoResponse}
                            />
                        </Tooltip>
                    </ChatInputOptionWrap>
                </FooterChatBox>
            </FooterContainer>
        </Container>
    ) : (
        <Container>
            <Header>
                <HeaderPlaceholder />
                <HeaderTitle>PDF 리뷰</HeaderTitle>
                <HeaderPlaceholder />
            </Header>
            <BodyContainer ref={bodyScrollRef} style={{ width: "100%", height: "100%" }}>
                <PDFChatFileUploadView user={props.user} reference={props.reference} />
            </BodyContainer>
        </Container>
    );
}

export default PDFChatSidebar;
